import React from "react";
import { Crosshair, ShieldCheck, Dna, Snowflake, FileText, MapPin } from "lucide-react";
import usePageMeta from "../lib/usePageMeta";

const sections = [
  {
    icon: Crosshair,
    title: "Batch Documentation",
    body: "Every Peptigen Labs product is presented with clear strength, storage guidance, batch identification, and research-use-only labelling.",
  },
  {
    icon: ShieldCheck,
    title: "Purity Verification",
    body: "Suppliers are vetted with documentation in mind. Where available, a Certificate of Analysis (COA) can be uploaded against a product or supplied on request.",
  },
  {
    icon: Snowflake,
    title: "Cold Storage & Handling",
    body: "Lyophilised material is held at 2-8°C in our facility. Dispatch packaging is sealed and padded — lyophilised material remains stable for short UK transit at ambient temperature.",
  },
  {
    icon: Dna,
    title: "Supplier Screening",
    body: "Sourcing is selected for documentation rigour and consistency rather than the cheapest available material.",
  },
  {
    icon: FileText,
    title: "Packaging Integrity",
    body: "Tamper-evident packaging, clear labels, and SKU/batch identifiers across all dispatched units.",
  },
  {
    icon: MapPin,
    title: "Research-Use Transparency",
    body: "Every product page, label, and order acknowledgement reaffirms the strict research-use-only positioning of all Peptigen Labs products.",
  },
];

export default function Quality() {
  usePageMeta("Quality Standards", "British supply. Research-grade standards.");
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="quality-page">
      <div className="mb-12 max-w-3xl">
        <div className="pg-micro mb-3">// Quality Standards</div>
        <h1 className="text-[#F7FAFC] text-4xl lg:text-5xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
          British supply. Research-grade standards.
        </h1>
        <p className="text-[#9CA8B8] mt-4 leading-relaxed">
          Peptigen Labs is built around documentation rigour, careful sourcing, and clear,
          defensible labelling. Below is a summary of how we approach quality across the catalogue.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {sections.map(({ icon: Icon, title, body }) => (
          <div key={title} className="pg-card p-6" data-testid={`quality-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <Icon size={24} className="text-[#00C2D6] mb-4" />
            <h3 className="text-[#F7FAFC] pg-heading text-lg mb-2" style={{ fontFamily: "'Rajdhani'" }}>
              {title}
            </h3>
            <p className="text-[#9CA8B8] text-sm leading-relaxed">{body}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 pg-card p-8">
        <div className="pg-micro mb-2">// Certificates of Analysis</div>
        <h2 className="text-[#F7FAFC] text-2xl pg-heading mb-3" style={{ fontFamily: "'Rajdhani'" }}>
          COA Availability
        </h2>
        <p className="text-[#9CA8B8] text-sm leading-relaxed max-w-3xl">
          Certificates of Analysis may be uploaded by admin and displayed on selected product
          pages, or made available on request depending on batch documentation. Use the contact
          form to request batch-specific documentation.
        </p>
      </div>
    </div>
  );
}
